import useLoadingState from "~/ui-rtk/hooks/loading-state"
import { useCodeBlock, useConnect } from "./connect"
import { useCallback, useEffect, useMemo, useState } from "react"
import { UpdateCompanyBrandEventDto } from "~/api/dto/company-brand-event/update-company-brand-event.dto"
import { H4, H5 } from "~/ui-rtk/components/ui/typography"
import Breadcrumbs from "~/ui-rtk/components/layout/Breadcrumbs"
import { Field, FieldArray, Form, Formik } from "formik"
import { Card, Divider, Loader } from "~/ui-rtk/components/ui/common"
import { Button, Toggle } from "~/ui-rtk/components/ui/controls"
import {
  ClipboardIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from "@heroicons/react/20/solid"
import { toast } from "react-toastify"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import { PixelIdBadges } from "./components/PixelIdBadges"

export function SettingsBrandEventsPage() {
  const { isLoading, setIsLoading } = useLoadingState()
  const currentCompany = useCurrentCompany()
  const { formattedCodeBlock, codeBlock } = useCodeBlock(
    currentCompany?.id ?? "",
  )

  const {
    allBrandEvents,
    initialValues,
    isLoading: isDataLoading,
    updateSettings,
    getPixelData,
    savePixelData,
  } = useConnect()
  // Dynamic collapsed sections based on available groups
  const [collapsedSections, setCollapsedSections] = useState<
    Record<string, boolean>
  >({})

  const [pixelIds, setPixelIds] = useState<Record<string, string[]>>(() => {
    try {
      return getPixelData()
    } catch (error) {
      console.warn("Error loading pixel data:", error)
      return {}
    }
  })

  // Dynamic pixel IDs modified state based on available groups
  const [pixelIdsModified, setPixelIdsModified] = useState<
    Record<string, boolean>
  >({})

  // Dynamic grouping based on available brand events
  const groupedEvents = useMemo(() => {
    if (!allBrandEvents) return {}

    return allBrandEvents.reduce(
      (
        acc: Record<
          string,
          Array<(typeof allBrandEvents)[number] & { index: number }>
        >,
        event,
        index,
      ) => {
        const group = event.group.toLowerCase()
        if (!acc[group]) {
          acc[group] = []
        }
        acc[group].push({ ...event, index })
        return acc
      },
      {},
    )
  }, [allBrandEvents])

  // Get dynamic list of available groups
  const availableGroups = useMemo(
    () => Object.keys(groupedEvents).sort(),
    [groupedEvents],
  )

  useEffect(() => {
    document.title = "Marathon Data: Settings > Brand Events Settings"
  }, [])

  useEffect(() => {
    try {
      const newPixelData = getPixelData()
      if (newPixelData) {
        setPixelIds(newPixelData)
      }
    } catch (error) {
      console.warn("Error updating pixel data:", error)
    }
  }, [getPixelData])

  const handleUpdateSettings = async (values: {
    events: UpdateCompanyBrandEventDto[]
  }) => {
    setIsLoading(true)
    await updateSettings(values.events).catch(() => null)
    setIsLoading(false)
  }

  const toggleSection = (section: string) => {
    setCollapsedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }))
  }

  const handlePixelIdsChange = (platform: string, newPixelIds: string[]) => {
    setPixelIds(prev => ({
      ...prev,
      [platform]: newPixelIds,
    }))
    setPixelIdsModified(prev => ({ ...prev, [platform]: true }))
  }

  const savePixelIds = async (platform: string) => {
    await savePixelData(platform, pixelIds[platform] || [])
    setPixelIdsModified(prev => ({ ...prev, [platform]: false }))
  }

  const cancelPixelIds = (platform: string) => {
    const originalData = getPixelData()
    setPixelIds(prev => ({
      ...prev,
      [platform]: originalData[platform] || [],
    }))
    setPixelIdsModified(prev => ({ ...prev, [platform]: false }))
  }

  const handleCopyToClipboard = useCallback(() => {
    void navigator.clipboard
      .writeText(codeBlock)
      .then(() => {
        toast.success("Copied")
      })
      .catch(() =>
        toast.error("Failed to copy id to clipboard", {
          toastId: "clipboard-copy-company-id",
        }),
      )
  }, [codeBlock])

  const renderScriptCode = useCallback(() => {
    if (!currentCompany) {
      return null
    }

    return (
      <Card rounded className="bg-background-dark-shade relative">
        <pre>{formattedCodeBlock} </pre>
        <ClipboardIcon
          width={20}
          color="grey"
          className="absolute top-4 right-4 hover:cursor-pointer"
          onClick={handleCopyToClipboard}
        />
      </Card>
    )
  }, [currentCompany])

  const renderPlatformSection = useCallback(
    (
      platform: string,
      events: Array<
        typeof allBrandEvents extends (infer T)[] | undefined
          ? T & { index: number }
          : never
      >,
      values: { events: UpdateCompanyBrandEventDto[] },
      arrayHelpers: {
        replace: (index: number, value: UpdateCompanyBrandEventDto) => void
      },
    ) => {
      const platformKey = platform.toLowerCase()
      const isCollapsed = collapsedSections[platformKey] || false

      return (
        <div key={platform} className="mb-6">
          <div
            className="flex items-center gap-2 cursor-pointer p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            onClick={() => toggleSection(platformKey)}
          >
            {isCollapsed ? (
              <ChevronRightIcon className="w-5 h-5 text-gray-600" />
            ) : (
              <ChevronDownIcon className="w-5 h-5 text-gray-600" />
            )}
            <H5 className="text-gray-800 font-semibold capitalize">
              {platform}
            </H5>
            <span className="text-sm text-gray-500">
              ({events.length} events)
            </span>
          </div>
          <div className="border-t pt-4">
            <PixelIdBadges
              pixelIds={pixelIds[platformKey] || []}
              onPixelIdsChange={newPixelIds =>
                handlePixelIdsChange(platformKey, newPixelIds)
              }
              platform={platform}
            />

            {pixelIdsModified[platformKey] && (
              <div className="flex gap-2 mt-3 pt-3 border-t">
                <Button
                  type="button"
                  variant={{ variant: "solid", color: "blue" }}
                  size="xs"
                  onClick={() => savePixelIds(platformKey)}
                >
                  Save Pixel IDs
                </Button>
                <Button
                  type="button"
                  variant={{ variant: "outlined" }}
                  size="xs"
                  onClick={() => cancelPixelIds(platformKey)}
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>

          <div
            className={cn("overflow-hidden transition-all duration-300", {
              "max-h-0": isCollapsed,
              "max-h-none": !isCollapsed,
            })}
          >
            {!isCollapsed && (
              <div className="mt-3 space-y-4">
                {events.length > 0 && (
                  <div>
                    <table className="table table-zebra w-full">
                      <thead>
                        <tr>
                          <th>Turn On/Off</th>
                          <th>Conversion Event Name</th>
                          <th>Event Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {events.map(event => (
                          <tr key={event.index}>
                            <td>
                              <Field
                                type="text"
                                id={`events[${event.index}].isActive`}
                                name={`events[${event.index}].isActive`}
                                as={Toggle}
                                value={values.events[event.index]?.isActive}
                                onChange={(active: boolean) =>
                                  arrayHelpers.replace(event.index, {
                                    ...values.events[event.index],
                                    isActive: active,
                                  })
                                }
                              />
                            </td>
                            <td>{event.label}</td>
                            <td>-</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {events.length === 0 && (
                  <div className="p-4 text-center text-gray-500">
                    No {platform} events available
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )
    },
    [
      collapsedSections,
      toggleSection,
      pixelIds,
      pixelIdsModified,
      handlePixelIdsChange,
      savePixelIds,
      cancelPixelIds,
    ],
  )

  if (isDataLoading) {
    return <Loader />
  }
  return (
    <div className="px-6 pt-6">
      <div className="text-start">
        <Breadcrumbs
          items={[
            {
              slug: "/settings",
              name: "Settings",
            },
            {
              slug: "brand-events",
              name: "Your Brand Conversion Events",
            },
          ]}
        />
      </div>
      <div className="mt-4 text-start">
        <div className="flex justify-between items-center">
          <H4 className="text-start">
            Add custom conversion events to your ad platforms here
          </H4>
          <div>{renderScriptCode()}</div>
        </div>
        <Divider className="mt-4" />
        <Formik
          initialValues={
            initialValues as {
              events: UpdateCompanyBrandEventDto[]
            }
          }
          onSubmit={handleUpdateSettings}
        >
          {({ values }) => (
            <Form>
              <div className="mt-6">
                <FieldArray
                  name="events"
                  render={arrayHelpers => (
                    <div className="space-y-4">
                      {availableGroups.map(group =>
                        renderPlatformSection(
                          group.charAt(0).toUpperCase() + group.slice(1), // Capitalize group name
                          groupedEvents[group] || [],
                          values,
                          arrayHelpers,
                        ),
                      )}
                    </div>
                  )}
                />
              </div>

              <Divider className="my-6" />
              <div className="flex justify-end">
                <Button
                  variant={{ variant: "solid", color: "pink" }}
                  size="sm"
                  type="submit"
                  isLoading={isLoading || isDataLoading}
                >
                  Update
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  )
}
